import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { format } from "date-fns";
import { formatMoney } from "../utils/formatMoney.js";
import { PLAN_CARD } from "../copy/opsDescriptions.js";
import CompensationPayInstallmentModal from "./CompensationPayInstallmentModal.jsx";

const instStatusColor = {
  pending: "warning",
  paid: "success",
  skipped: "default",
  cancelled: "error",
};

export default function CompensationPlanCard({
  plan,
  jobId,
  processedBy,
  onPayInstallment,
  payLoading = false,
}) {
  const [payTarget, setPayTarget] = useState(null);

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" fontFamily="monospace">
                {plan.id}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatMoney(plan.totalAmount)} — customer compensation
              </Typography>
            </Box>
            <Chip label={plan.status} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {PLAN_CARD.intro}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {plan.reason}
          </Typography>
          {plan.adjustmentId && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Linked adjustment: {plan.adjustmentId}
            </Typography>
          )}

          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(plan.installments || []).map((inst) => (
                <TableRow key={inst.sequence}>
                  <TableCell>{inst.sequence}</TableCell>
                  <TableCell>
                    {inst.dueDate ? format(new Date(inst.dueDate), "PP") : "—"}
                  </TableCell>
                  <TableCell align="right">{formatMoney(inst.amount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={inst.status}
                      size="small"
                      color={instStatusColor[inst.status] || "default"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {inst.status === "pending" && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={payLoading}
                        onClick={() => setPayTarget(inst)}
                        sx={{ textTransform: "none" }}
                      >
                        Pay customer
                      </Button>
                    )}
                    {inst.status === "paid" && inst.proofFileName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Proof: {inst.proofFileName}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CompensationPayInstallmentModal
        open={Boolean(payTarget)}
        onClose={() => setPayTarget(null)}
        jobId={jobId}
        plan={plan}
        installment={payTarget}
        processedBy={processedBy}
        onPaid={() => {
          setPayTarget(null);
          onPayInstallment?.();
        }}
      />
    </>
  );
}
